/* global google */
import { concat } from '@ember/helper';
import { action, set } from '@ember/object';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import { didInsert } from '@ember/render-modifiers';
import Component from '@glimmer/component';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import { modifier } from 'ember-modifier';
import { getCssVariableValue } from 'frontend-utils/utils/css-variable';
import registerObserver from 'frontend-utils/utils/register-observer';
import { getGoogleMapsStyles } from 'lumen/utils/constants/google-maps-constants';
import { getHalfColoredMarkerSvg } from 'lumen/utils/map-markers';
import { timeout } from 'lumen/utils/waiters';

import styles from './competitor-map.module.css';

interface SettingsCompetitorsCompetitorMapSignature {
  Args: {
    compsets: unknown;
    hotel: unknown;
    tempCompetitor: unknown;
  };
  Element: HTMLDivElement;
}

export default class SettingsCompetitorsCompetitorMap extends Component<SettingsCompetitorsCompetitorMapSignature> {
  createLegend = modifier((legendElement) => {
    // @ts-expect-error: Incorrect type
    if (!this.args.compsets.isRMS) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      this.map.controls[google.maps.ControlPosition.RIGHT_BOTTOM].push(
        legendElement,
      );
    }
  });

  map = null;

  markers = {};

  tempMarker = null;

  clearAndRebuildMapMarkersTask = task({ restartable: true }, async () => {
    await timeout('extra-short');

    this.purgeMarkers();
    this.markOwnHotel();
    this.markOtherHotels();
    this.setBoundsForMap();
  });

  constructor() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line prefer-rest-params
    super(...arguments);

    registerObserver(
      this,
      [
        'args.compsets.uniqueCompetitors.@each.hotelinfo',
        'args.compsets.uniqueCompetitors.@each.isPrimary',
        'args.compsets.uniqueCompetitors.@each.isSecondary',
        'args.compsets.uniqueCompetitors.length',
        'args.hotel.id',
      ],
      // eslint-disable-next-line @typescript-eslint/unbound-method
      this.competitorsChanged,
    );
    registerObserver(
      this,
      ['args.compsets.uniqueCompetitors.@each.highlighted'],
      // eslint-disable-next-line @typescript-eslint/unbound-method
      this.highlightsChanged,
    );
  }

  /**
   * Centers the view on the hotel linked to the subscription of the currently logged-in user
   */
  centerMapOnOwnHotel(): void {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const ownHotelInfo = this.args.hotel?.hotelinfo;
    const ownMarker = this.getMarkerByHotelinfo(ownHotelInfo);

    // Reset view to own hotel in case there are no competitors
    const center = {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      lat: ownMarker.position.lat(),
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      lng: ownMarker.position.lng(),
    };

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.map.setCenter(center);
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.map.setZoom(14);
  }

  competitorsChanged(): void {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.clearAndRebuildMapMarkersTask.perform();
  }

  /**
   * Resets the view's bounds to fit all markers
   */
  fitMapToMarkers(): void {
    // https://stackoverflow.com/a/19304625
    const bounds = new google.maps.LatLngBounds();

    // All competitor markers including your own hotel
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    const markers = Object.keys(this.markers).map((id) => this.markers[id]);

    // Add occasional temp competitor
    if (this.tempMarker) {
      markers.push(this.tempMarker);
    }

    // Extends bounds with available markers
    markers.forEach((marker) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      bounds.extend(marker.getPosition());
    });
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.map.fitBounds(bounds);
  }

  /**
   * Depending on the compset the competitor is in, use the appropriate icons
   * @param  {Object} competitor Competitor object to find an icon for
   * @return {Object}            An icon object containing url and its scale
   */
  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getIconForCompetitor(competitor) {
    let iconConfig = {
      path: google.maps.SymbolPath.CIRCLE,
      fillOpacity: 1,
      scale: 8,
      strokeWeight: 2,
      strokeColor: getCssVariableValue('--prism-color-border-inverse-emphasis'),
    };

    // Lengthy conditions because brocoli-asset-rev cannot handle templated urls
    if (
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      competitor.isPrimary &&
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      competitor.isSecondary &&
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      !competitor.highlighted
    ) {
      iconConfig = {
        // @ts-expect-error: Incorrect type
        url: getHalfColoredMarkerSvg(
          getCssVariableValue(
            '--prism-color-chart-accent-primary-figure-default',
          ),
          getCssVariableValue('--prism-color-chart-categorical-02'),
        ),
        scaledSize: new google.maps.Size(32, 32),
        anchor: new google.maps.Point(16, 16),
      };
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    } else if (competitor.isPrimary) {
      // @ts-expect-error: Incorrect type
      iconConfig.fillColor = getCssVariableValue(
        '--prism-color-chart-accent-primary-figure-default',
      );
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    } else if (competitor.isSecondary) {
      // @ts-expect-error: Incorrect type
      iconConfig.fillColor = getCssVariableValue(
        '--prism-color-chart-categorical-02',
      );
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    } else if (competitor.isRMS) {
      // @ts-expect-error: Incorrect type
      iconConfig.fillColor = getCssVariableValue(
        '--prism-color-chart-accent-primary-figure-default',
      );
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    } else if (competitor.isTemp) {
      // @ts-expect-error: Incorrect type
      iconConfig.fillColor = getCssVariableValue(
        '--prism-color-icon-info-default',
      );
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (competitor.highlighted) {
      // @ts-expect-error: Incorrect type
      iconConfig.fillColor = getCssVariableValue(
        '--prism-color-chart-accent-primary-figure-subdued',
      );
      iconConfig.scale = 10;
    }
    return iconConfig;
  }

  /**
   * Search function which retrieves a Marker object for a given hotelInfo object
   * @param  {Object} hotelinfo The hotelinfo of the hotel for which to search its Marker
   * @return {Object}           Retrieves the Marker object that was found by a given hotelinfo
   */
  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getMarkerByHotelinfo(hotelinfo) {
    let marker;

    Object.keys(this.markers).forEach((id) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (id === hotelinfo.id) {
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        marker = this.markers[id];
      }
    });

    return marker;
  }

  /**
   * Highlights a competitor on the map
   * @param  {Object} competitor Competitor object to highlight
   * @param  {Object} marker     Marker object to change for the highlight
   */
  // @ts-expect-error: Incorrect type
  highlightCompetitor(competitor, marker): void {
    const icon = this.getIconForCompetitor(competitor);

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (competitor.highlighted) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      marker.setZIndex(30);
    } else {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      marker.setZIndex(10);
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    marker.setIcon(icon);
  }

  // If a highlight is triggered externally (e.g. from the list) set new icons
  highlightsChanged(): void {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    this.args.compsets?.uniqueCompetitors?.forEach((competitor) => {
      // Search up until you find the associated icon for the competitor
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      const marker = this.markers[competitor.hotelinfo.id];

      if (marker) {
        this.highlightCompetitor(competitor, marker);
      }
    });
  }

  /**
   * Marks all hotels in from all compsets
   */
  markOtherHotels(): void {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    this.args.compsets?.uniqueCompetitors?.forEach((competitor) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const { hotelinfo } = competitor;
      const icon = this.getIconForCompetitor(competitor);

      const marker = this.setMarker(hotelinfo, icon, 10);

      // Open name tags when you hover over hotel in map view
      marker.addListener('mouseover', () => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        set(competitor, 'highlighted', true);
      });
      marker.addListener('mouseout', () => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        set(competitor, 'highlighted', false);
      });
    });
  }

  /**
   * Marks the hotel linked to the subscription of the currently logged-in user
   */
  markOwnHotel(): void {
    const icon = {
      path: google.maps.SymbolPath.CIRCLE,
      fillOpacity: 1,
      scale: 6,
      strokeWeight: 4,
      fillColor: getCssVariableValue(
        '--prism-color-chart-accent-secondary-figure-emphasis',
      ),
      strokeColor: getCssVariableValue(
        '--prism-color-chart-accent-secondary-figure-emphasis',
      ),
    };

    // @ts-expect-error: Incorrect type
    this.setMarker(this.args.hotel?.hotelinfo, icon, 20);
  }

  /**
   * Clears all markers from the map.
   * This is required to be able to correctly update all markers on the map
   */
  purgeMarkers(): void {
    Object.keys(this.markers).forEach((id) => {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      this.markers[id].setMap(null);
      // @ts-expect-error: Incorrect type
      delete this.markers[id];
    });
  }

  /**
   * Mid-level function for either fitting map to all competitors
   * or centering on own hotel
   */
  setBoundsForMap(): void {
    // Only fit map to marker bounds when there are markers present
    if (Object.keys(this.markers).length > 1 || this.args.tempCompetitor) {
      this.fitMapToMarkers();
    } else {
      this.centerMapOnOwnHotel();
    }
  }

  /**
   * Sets a marker on the map
   * @param {hotelInfo} hotelinfo : hotelinfo to get lat and long from
   * @param {string}    marker    : name of the marker
   * @param {object}    icon      : icon to be shown
   */
  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  setMarker(hotelinfo, icon, zIndex, tempMarker = false) {
    // Clear the old marker
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (this.markers[hotelinfo.id]) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      this.markers[hotelinfo.id].setMap(null);
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      delete this.markers[hotelinfo.id];
    }

    // This is where all markers are to be created, nowhere else!
    const newMarker = new google.maps.Marker({
      position: {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        lat: hotelinfo.latitude,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        lng: hotelinfo.longitude,
      },
      map: this.map,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      title: hotelinfo.name,
      optimized: false,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      zIndex,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      icon,
    });

    if (!tempMarker) {
      // Save the marker by the hotel's unique ID for later operations
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      set(this, `markers.${hotelinfo.id}`, newMarker);
    }

    // Return the marker in case the caller wants to bind events listeners to it
    return newMarker;
  }

  /**
   * This initializes Google Maps in the appropriate container
   */
  @action
  // @ts-expect-error: Incorrect type
  addMap(mapElement): void {
    const ownHotel = this.args.hotel;

    set(
      this,
      'map',
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      new google.maps.Map(mapElement, {
        center: new google.maps.LatLng(
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
          ownHotel.hotelinfo?.latitude,
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
          ownHotel.hotelinfo?.longitude,
        ),
        disableDefaultUI: true,
        zoom: 14,
        // disable zooming
        zoomControl: false,
        zoomScroll: false,
        scaleControl: true,
        disableDoubleClickZoom: false,
        // disable mouse dragging
        draggable: false,
        draggableCursor: 'cursor',
        zoomControlOptions: {
          position: google.maps.ControlPosition.LEFT_BOTTOM,
        },
        styles: [
          {
            featureType: 'administrative',
            elementType: 'labels',
            stylers: [
              {
                visibility: 'off',
              },
            ],
          },
          {
            featureType: 'poi',
            elementType: 'labels',
            stylers: [
              {
                visibility: 'off',
              },
            ],
          },
          {
            featureType: 'road',
            elementType: 'labels',
            stylers: [
              {
                visibility: 'off',
              },
            ],
          },
          {
            featureType: 'transit',
            elementType: 'labels',
            stylers: [
              {
                visibility: 'off',
              },
            ],
          },
          // @ts-expect-error: Incorrect type
          {
            featureType: 'water',
            elementType: 'geometry.fill',
          },
          ...getGoogleMapsStyles().BLACK_AND_WHITE,
        ],
      }),
    );
    // https://stackoverflow.com/a/21418371
    // Update every time the map zooms (fixes disappearing markers in map only)
    // @ts-expect-error: Incorrect type
    google.maps.event.addListener(this.map, 'zoom_changed', () => {
      setTimeout(() => {
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
        const cnt = this.map.getCenter();
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        cnt.e += 0.000001;
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        this.map.panTo(cnt);
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        cnt.e -= 0.000001;
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        this.map.panTo(cnt);
      }, 500); // Any lower value might make the markers scale too big/small
    });
  }

  // Create map and markers at this stage to prevent invisible temp competitor markers
  @action
  // @ts-expect-error: Incorrect type
  didInsertMapElement(mapElement): void {
    this.addMap(mapElement);
    this.markOwnHotel();
    this.markOtherHotels();
    // @ts-expect-error: Incorrect type
    google.maps.event.trigger(this.map, 'resize');
    this.setBoundsForMap();
  }

  <template>
    <div
      class={{concat styles.competitors-google-maps-container " " "h-full"}}
      id="competitor-map"
      {{didInsert this.didInsertMapElement}}
      ...attributes
    ></div>

    <div class="hidden">
      <div
        class="text-200--bold rounded-100 bg-neutral-default m-200 grid gap-200 p-200"
        {{this.createLegend}}
      >
        <div>
          <span
            class="identifier-bullet bg-chart-accent-primary-figure-default"
          ></span>
          {{t "settings.competitors.primary_compset"}}
        </div>
        <div>
          <span class="identifier-bullet bg-chart-categorical-02"></span>
          {{t "settings.competitors.secondary_compset"}}
        </div>
        <div>
          <span
            class="identifier-bullet bg-chart-accent-secondary-figure-emphasis"
          ></span>
          {{t "general.map.legend.your_hotel"}}
        </div>
      </div>
    </div>
  </template>
}
